import React, { useImperativeHandle, useEffect, useMemo } from 'react';
// @ts-ignore
import { useSelector } from 'umi';
import ImagesUploaderBtn from '@/pages/home/components/ImagesUploader/components/ImagesUploaderBtn';
import StatusImageList from '@/pages/home/components/ImagesUploader/components/StatusImageList';
import {
  IStatusImage,
  ImageError,
  ImageStatus,
} from '@/pages/home/components/ImagesUploader/components/StatusImageItem';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import useCOS from '@/pages/home/components/ImagesUploader/_hooks/useMockCOS';
import { noop, getUniqueKey } from '@/pages/home/utils/utils';
import { file2DataURL } from '@/pages/home/utils/file';
import { compressImage } from '@/pages/home/utils/image';
import { IImageErrorInfo, IUploadSuccessImage } from '@/pages/home/components/ImagesUploader/types.d';
import { useGetState } from 'ahooks';
import { rest } from 'lodash';

// 图片压缩默时，预期最大可接受的图片大小 6M，但是由于不能丢失太多图片清晰度原因，所以不一定能把图片大小压缩到这个数值以下，暂时图像质量最小为0.6，如果压缩后的图像质量小于或等于0.6时，仍然没压缩到可接受的最大图片大小时，就不会继续压缩了
// const DEFAULT_MAX_SIZE = 6 * 1024 * 1024;
const DEFAULT_MAX_SIZE = 300 * 1024;

export default function ImagesUploaderCustomm(props: any) {
  const { formatMessage } = useIntlExtend();

  const {
    // 最多上传图片数量
    max = 8,
    // 是否允许批量上传
    multiple = true,
    // 接受的图片格式
    accept = 'image/*',
    // 上传模式
    // type = UploadMode.COS,
    // 是否启用上传进度功能
    enabledProgress = true,
    // 是否启用图片压缩功能
    enabledCompress = true,
    // 启用图片压缩时，预计最大接受的图片大小，单位为B
    maxSize = DEFAULT_MAX_SIZE,
    // 上传模式为COS时，取消是否自动从COS删除本次临时上传的图片
    // autoDeleteWhenCancel = true,
    // 上传模式为COS时，文件存储路径
    cosPath = '/images/feedback',
    // 图片压缩报错提示文案
    compressErrorMsg = formatMessage({
      id: 'dbh_compress_image_error_msg',
      defaultMessage: 'Image compress error',
    }),
    // 图片上传网络报错提示文案
    networkErrorMsg = formatMessage({
      id: 'dbh_network_error_msg',
      defaultMessage: 'Network error',
    }),
    // 未知异常报错提示文案
    unknowErrorMsg = formatMessage({
      id: 'dbh_unknow_error_msg',
      defaultMessage: 'Unknow error',
    }),
    // 上传成功回调，通过该回调获取 图片数据
    onUploadSuccess = noop,
    // 删除成功回调，通过该回调 更新 父组件的图片数据
    onDeleteSuccess = noop,
    ref,
    editImagesData,
    handleDeleteEditImage,
    isPublicTrueData = false,
    ...rest
  } = props;

  // console.log('ImagesUploaderCustomm', props);

  const { userInfo } = useSelector((state: any) => ({
    userInfo: state?.home?.userInfo,
  }));

  const { putObject = noop, deleteObject = noop } = useCOS();

  const [imagesData, setImagesData, getImagesData] = useGetState<IStatusImage[]>([]);

  const isUploadDisabled = useMemo(() => imagesData?.length >= max, [imagesData, max]);

  const isImagesAllSetted = useMemo(() => {
    if (!Array.isArray(imagesData)) return false;
    return !imagesData?.some((item) => item?.status === ImageStatus.Loading);
  }, [imagesData]);

  const updateImageData = (imageDataIndex: number, imageData: IStatusImage, imagesData: IStatusImage[]) => {
    const imageDataTemp = imagesData?.[imageDataIndex];
    const newImagesData = [
      ...imagesData?.slice(0, imageDataIndex),
      {
        ...imageDataTemp,
        ...imageData,
      },
      ...imagesData?.slice(imageDataIndex + 1),
    ];
    setImagesData(newImagesData);
  };

  const deleteImageData = (imagesData: IStatusImage[], uniqKey: string) => {
    if (!Array.isArray(imagesData)) return;
    const index = imagesData?.findIndex((item) => item?.uniqKey === uniqKey);
    if (index === -1) return;
    const newImagesData = [...imagesData?.slice(0, index), ...imagesData?.slice(index + 1)];
    setImagesData(newImagesData);
  };

  /**
   * 获取文件列表，处理单张上传、批量上传，统一转为批量上传模式，并且处理最大上传图片的限制
   * @param e 文件上传input控件的 change事件的 事件对象
   * @returns 文件列表
   */
  const getFiles = (e: any): File[] => {
    if (multiple) {
      // 当前图片未达到数量上限，但是本次上传的多张图片 加起来超过数量上限时，对本次上传的图片做截取，保证上传的图片数量不超过上限
      const num = max - imagesData?.length;
      const temp = Array.from(e?.target?.files)?.slice(0, num);
      return (temp || []) as File[];
    }
    const file = e?.target?.files?.[0];
    return file ? [file] : [];
  };

  const getErrorInformation = (error: any): IImageErrorInfo => {
    const errorMap: {
      [key: string]: IImageErrorInfo;
    } = {
      COS_ERROR: {
        error: ImageError.NetworkError,
        errorMsg: networkErrorMsg,
      },
      COMPRESS_ERROR: {
        error: ImageError.CompressError,
        errorMsg: compressErrorMsg,
      },
    };
    return (
      errorMap[error?.code] || {
        error: ImageError.UnknowError,
        errorMsg: unknowErrorMsg,
      }
    );
  };

  const uploadImg = (
    imgFile: File,
    options: {
      onProgress: (payload: any) => void;
      onSuccess: (payload: any) => void;
      onError: (error: any) => void;
    },
  ) => {
    const { onProgress = noop, onSuccess = noop, onError = noop } = options || {};

    (async () => {
      try {
        let file = imgFile;
        // dataURL格式数据，需要使用 解开注释即可
        let dataURL = '';
        if (enabledCompress) {
          const compressedRes = await compressImage(imgFile, {
            maxSize: maxSize || DEFAULT_MAX_SIZE,
          });
          if (compressedRes?.file) {
            file = compressedRes?.file;
          }
          if (compressedRes?.dataURL) {
            dataURL = compressedRes?.dataURL;
          }
        } else {
          dataURL = await file2DataURL(file);
        }
        const path = `${cosPath}/${userInfo?.username}-${Date.now()}-${file?.name || 'img'}`;
        const fileName = await putObject({
          path,
          file,
          onProgress: (progressData: any) => {
            if (!enabledProgress) return;
            onProgress?.(progressData?.percent * 100);
          },
        });
        onSuccess?.({
          path: fileName,
          file,
          dataURL,
        });
      } catch (exception: any) {
        console.warn(exception);
        onError?.(exception);
      }
    })();
  };

  const deleteImg = async (image: IStatusImage) => {
    try {
      if (image?.status === ImageStatus.Success) {
        const data = { path: image?.path || '' };
        await deleteObject(data);
        onDeleteSuccess?.(data);
      }
      deleteImageData(imagesData, image?.uniqKey || '');
    } catch (exception) {
      console.warn(exception);
    }
  };

  const deleteImgs = async () => {
    for (const imageData of imagesData) {
      try {
        await deleteImg(imageData);
      } catch (exception) {
        console.warn(exception);
      }
    }
  };

  const clear = (e: any) => {
    if (e?.target) {
      e.target.value = null;
    }
  };

  const handleImageUploaderChange = (e: any) => {
    if (isUploadDisabled) {
      e?.preventDefault();
      return;
    }
    const imgFiles = getFiles(e);

    const comingImagesData: IStatusImage[] = [];

    for (const imgFile of imgFiles) {
      const uniqKey = getUniqueKey();

      comingImagesData?.push({
        uniqKey,
        src: '',
        path: '',
        file: undefined,
        status: ImageStatus.Loading,
        progress: enabledProgress ? 0 : 100,
        error: ImageError.None,
        errorMsg: '',
      });

      uploadImg(imgFile, {
        onProgress: (progress: number) => {
          if (!enabledProgress) return;
          // console.log(`${imgFile?.name}文件 上传进度: ${progress}`);
          const latestImagesData = getImagesData();
          const imageDataIndex = latestImagesData?.findIndex((item) => item?.uniqKey === uniqKey);
          const imageData = latestImagesData?.[imageDataIndex];
          if (imageData) {
            const newImageData = {
              ...imageData,
              // 在onSuccess回调里 才 设置进度为100%，onProgress回调里 最多进度达到99%即可
              progress: Math.min(progress || 0, 99),
            };
            updateImageData(imageDataIndex, newImageData, latestImagesData);
          }
        },
        onSuccess: (payload: any) => {
          const latestImagesData = getImagesData();
          const imageDataIndex = latestImagesData?.findIndex((item) => item?.uniqKey === uniqKey);
          const imageData = latestImagesData?.[imageDataIndex];
          if (imageData) {
            const { path, file, dataURL } = payload || {};
            const newImageData = {
              ...imageData,
              src: dataURL,
              path,
              file,
              status: ImageStatus.Success,
              progress: 100,
              error: ImageError.None,
              errorMsg: '',
            };
            updateImageData(imageDataIndex, newImageData, latestImagesData);
          }
        },
        onError: (error) => {
          const latestImagesData = getImagesData();
          const imageDataIndex = latestImagesData?.findIndex((item) => item?.uniqKey === uniqKey);
          const imageData = latestImagesData?.[imageDataIndex];
          const errorInfo = getErrorInformation(error);
          const newImageData = {
            ...imageData,
            status: ImageStatus.Error,
            error: errorInfo?.error,
            errorMsg: errorInfo?.errorMsg || '',
          };
          updateImageData(imageDataIndex, newImageData, latestImagesData);
        },
      });
    }

    const newImagesData = [...imagesData, ...comingImagesData];

    setImagesData(newImagesData);

    clear(e);
  };

  useImperativeHandle(
    ref,
    () => ({
      deleteImgs,
    }),
    [deleteImgs],
  );

  useEffect(() => {
    if (!isImagesAllSetted) return;
    const successImages: IUploadSuccessImage[] = [];
    const errorImages: IUploadSuccessImage[] = [];
    for (const imageData of imagesData) {
      const image: IUploadSuccessImage = {
        src: imageData?.src || '',
        path: imageData?.path || '',
        file: imageData?.file,
      };
      if (imageData?.status === ImageStatus.Success) {
        successImages?.push(image);
      } else {
        errorImages?.push(image);
      }
    }
    onUploadSuccess?.({ success: successImages, error: errorImages });
  }, [isImagesAllSetted]);

  return (
    <>
      <ImagesUploaderBtn
        multiple={multiple}
        accept={accept}
        disabled={isUploadDisabled}
        onChange={handleImageUploaderChange}
        isNeedUnloadModalDesc={false}
        {...rest}
      />
      <StatusImageList
        imagesData={imagesData}
        onDelete={deleteImg}
        editImagesData={editImagesData}
        handleDeleteEditImage={handleDeleteEditImage}
      />
    </>
  );
}
